import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../../utils/textUtils';
import {
  Box,
  Typography,
  Switch,
  Paper,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  styled
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EmailIcon from '@mui/icons-material/Email';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import UpdateIcon from '@mui/icons-material/Update';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CampaignIcon from '@mui/icons-material/Campaign';
import InfoIcon from '@mui/icons-material/Info';
import SendIcon from '@mui/icons-material/Send';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import pushNotificationService from '../../services/pushNotificationService';

// Estilos personalizados
const NotificationCard = styled(Card)(({ theme }) => ({
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
  }
}));

const NotificationSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: 'rgba(240, 193, 75, 0.08)'
    }
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: theme.palette.primary.main
  }
}));

const NotificationSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingPush, setTestingPush] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [initialized, setInitialized] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: false,
    smsNotifications: false,
    projectUpdates: true,
    taskAssignments: true,
    progressReports: true,
    approvalRequests: true,
    systemAnnouncements: true,
    dailyDigest: false,
    weeklyDigest: true
  });

  const fetchNotificationSettings = useCallback(async () => {
    if (!user || initialized) return;
    
    // Ensure user has an id property
    const userId = user.id || user.userId;
    if (!userId) {
      console.error('User ID is undefined');
      setAlert({
        open: true,
        message: t('ID PENGGUNA TIDAK VALID'),
        severity: 'error'
      });
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      // In a real app, you would fetch user notification settings from the API
      // For now, we'll use mock data
      const response = await api.get(`/users/${userId}/notification-settings`).catch(() => {
        // If API endpoint doesn't exist yet, use default settings
        return { 
          data: {
            emailNotifications: true,
            pushNotifications: false,
            smsNotifications: false,
            projectUpdates: true,
            taskAssignments: true,
            progressReports: true,
            approvalRequests: true,
            systemAnnouncements: true,
            dailyDigest: false,
            weeklyDigest: true
          }
        };
      });
      
      setSettings(response.data);
      setInitialized(true);
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    } finally {
      setLoading(false);
    }
  }, [user, initialized]);

  useEffect(() => {
    fetchNotificationSettings();
  }, [fetchNotificationSettings]);

  // Initialize push notification service
  useEffect(() => {
    const initializePushService = async () => {
      try {
        const supported = pushNotificationService.isNotificationSupported();
        setPushSupported(supported);
        
        if (supported) {
          const permission = pushNotificationService.getPermissionStatus();
          setPushPermission(permission);
          
          // Initialize service
          await pushNotificationService.initialize();
        }
      } catch (error) {
        console.error('Failed to initialize push notification service:', error);
      }
    };

    initializePushService();
  }, []);

  const handleChange = async (event) => {
    const { name, checked } = event.target;
    
    // Special handling for push notifications
    if (name === 'pushNotifications' && checked) {
      try {
        // Request permission if not granted
        if (pushPermission !== 'granted') {
          const permission = await pushNotificationService.requestPermission();
          setPushPermission(permission);
          
          if (permission !== 'granted') {
            setAlert({
              open: true,
              message: t('IZIN NOTIFIKASI PUSH DIPERLUKAN UNTUK MENGAKTIFKAN FITUR INI'),
              severity: 'warning'
            });
            return;
          }
        }
        
        // Subscribe to push notifications
        await pushNotificationService.subscribeToPush();
        
        setAlert({
          open: true,
          message: t('NOTIFIKASI PUSH BERHASIL DIAKTIFKAN'),
          severity: 'success'
        });
      } catch (error) {
        console.error('Error enabling push notifications:', error);
        setAlert({
          open: true,
          message: t('GAGAL MENGAKTIFKAN NOTIFIKASI PUSH'),
          severity: 'error'
        });
        return;
      }
    } else if (name === 'pushNotifications' && !checked) {
      try {
        // Unsubscribe from push notifications
        await pushNotificationService.unsubscribeFromPush();
        
        setAlert({
          open: true,
          message: t('NOTIFIKASI PUSH BERHASIL DINONAKTIFKAN'),
          severity: 'success'
        });
      } catch (error) {
        console.error('Error disabling push notifications:', error);
        setAlert({
          open: true,
          message: t('GAGAL MENONAKTIFKAN NOTIFIKASI PUSH'),
          severity: 'error'
        });
        return;
      }
    }
    
    setSettings({
      ...settings,
      [name]: checked
    });
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      // Guardar configuración en el backend
      await api.put(`/users/${user.id}/notification-settings`, settings);
      
      setAlert({
        open: true,
        message: t('PENGATURAN NOTIFIKASI BERHASIL DISIMPAN'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving notification settings:', error);
      setAlert({
        open: true,
        message: t('GAGAL MENYIMPAN PENGATURAN NOTIFIKASI'),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    
    try {
      await api.post(`/users/${user.id}/test-email-notification`);
      
      setAlert({
        open: true,
        message: t('EMAIL TEST BERHASIL DIKIRIM! SILAKAN PERIKSA KOTAK MASUK ANDA.'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error sending test email:', error);
      setAlert({
        open: true,
        message: t('GAGAL MENGIRIM EMAIL TEST. PASTIKAN EMAIL ANDA VALID DAN PENGATURAN EMAIL AKTIF.'),
        severity: 'error'
      });
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestPush = async () => {
    setTestingPush(true);
    
    try {
      // Check if push notifications are supported and enabled
      if (!pushSupported) {
        setAlert({
          open: true,
          message: t('NOTIFIKASI PUSH TIDAK DIDUKUNG DI BROWSER INI'),
          severity: 'warning'
        });
        return;
      }

      if (pushPermission !== 'granted') {
        setAlert({
          open: true,
          message: t('IZIN NOTIFIKASI PUSH BELUM DIBERIKAN'),
          severity: 'warning'
        });
        return;
      }

      // Send test push notification
      await pushNotificationService.testPushNotification();
      
      setAlert({
        open: true,
        message: t('NOTIFIKASI PUSH TEST BERHASIL DIKIRIM!'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error sending test push notification:', error);
      setAlert({
        open: true,
        message: t('GAGAL MENGIRIM NOTIFIKASI PUSH TEST'),
        severity: 'error'
      });
    } finally {
      setTestingPush(false);
    }
  };
  
  // Obtener información sobre cada tipo de notificación
  const getNotificationInfo = (type) => {
    const info = {
      emailNotifications: t('DAPATKAN PEMBERITAHUAN MELALUI EMAIL UNTUK SEMUA AKTIVITAS PENTING'),
      pushNotifications: t('DAPATKAN PEMBERITAHUAN LANGSUNG DI BROWSER ATAU APLIKASI MOBILE'),
      smsNotifications: t('DAPATKAN PEMBERITAHUAN SMS UNTUK PERINGATAN PENTING'),
      projectUpdates: t('PERUBAHAN STATUS PROYEK, PEMBARUAN DOKUMEN, DAN INFORMASI PROYEK LAINNYA'),
      taskAssignments: t('PEMBERITAHUAN SAAT TUGAS BARU DITUGASKAN KEPADA ANDA'),
      progressReports: t('LAPORAN KEMAJUAN MINGGUAN DAN BULANAN'),
      approvalRequests: t('PERMINTAAN PERSETUJUAN UNTUK DOKUMEN, ANGGARAN, ATAU PERUBAHAN'),
      systemAnnouncements: t('PENGUMUMAN SISTEM DAN PEMBARUAN PLATFORM'),
      dailyDigest: t('RINGKASAN HARIAN DARI SEMUA AKTIVITAS'),
      weeklyDigest: t('RINGKASAN MINGGUAN DARI SEMUA AKTIVITAS')
    };
    return info[type] || '';
  };
  
  // Obtener ícono para cada tipo de notificación
  const getNotificationIcon = (type) => {
    const icons = {
      emailNotifications: <EmailIcon />,
      pushNotifications: <PhoneAndroidIcon />,
      smsNotifications: <CampaignIcon />,
      projectUpdates: <UpdateIcon />,
      taskAssignments: <AssignmentIcon />,
      progressReports: <AssessmentIcon />,
      approvalRequests: <CheckCircleIcon />,
      systemAnnouncements: <NotificationsIcon />,
      dailyDigest: <UpdateIcon />,
      weeklyDigest: <UpdateIcon />
    };
    return icons[type] || <NotificationsIcon />;
  };

  const handleCloseAlert = () => {
    setAlert(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('PENGATURAN NOTIFIKASI')}
        </Typography>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? t('MENYIMPAN...') : t('SIMPAN PENGATURAN')}
        </Button>
      </Box>
      
      <Grid container spacing={3}>
        {/* Saluran Notifikasi */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <NotificationsIcon sx={{ mr: 1 }} /> {t('SALURAN NOTIFIKASI')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('PILIH BAGAIMANA ANDA INGIN MENERIMA NOTIFIKASI')}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <NotificationCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EmailIcon sx={{ mr: 1, color: '#5D4037' }} />
                        <Typography variant="subtitle2">{t('EMAIL')}</Typography>
                      </Box>
                      <NotificationSwitch
                        checked={settings.emailNotifications}
                        onChange={handleChange}
                        name="emailNotifications"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {getNotificationInfo('emailNotifications')}
                    </Typography>
                    {settings.emailNotifications && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={testingEmail ? <CircularProgress size={16} /> : <SendIcon />}
                        onClick={handleTestEmail}
                        disabled={testingEmail}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        {testingEmail ? t('MENGIRIM...') : t('TEST EMAIL')}
                      </Button>
                    )}
                  </CardContent>
                </NotificationCard>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <NotificationCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhoneAndroidIcon sx={{ mr: 1, color: '#5D4037' }} />
                        <Typography variant="subtitle2">{t('NOTIFIKASI PUSH')}</Typography>
                      </Box>
                      <NotificationSwitch
                        checked={settings.pushNotifications}
                        onChange={handleChange}
                        name="pushNotifications"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {getNotificationInfo('pushNotifications')}
                    </Typography>
                    {!pushSupported && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        {t('NOTIFIKASI PUSH TIDAK DIDUKUNG DI BROWSER INI')}
                      </Alert>
                    )}
                    {pushSupported && pushPermission === 'denied' && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {t('IZIN NOTIFIKASI PUSH DITOLAK. SILAKAN AKTIFKAN DI PENGATURAN BROWSER.')}
                      </Alert>
                    )}
                    {settings.pushNotifications && pushSupported && pushPermission === 'granted' && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={testingPush ? <CircularProgress size={16} /> : <SendIcon />}
                        onClick={handleTestPush}
                        disabled={testingPush}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        {testingPush ? t('MENGIRIM...') : t('TEST PUSH')}
                      </Button>
                    )}
                  </CardContent>
                </NotificationCard>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <NotificationCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CampaignIcon sx={{ mr: 1, color: '#5D4037' }} />
                        <Typography variant="subtitle2">{t('SMS')}</Typography>
                      </Box>
                      <NotificationSwitch
                        checked={settings.smsNotifications}
                        onChange={handleChange}
                        name="smsNotifications"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {getNotificationInfo('smsNotifications')}
                    </Typography>
                  </CardContent>
                </NotificationCard>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Jenis Notifikasi */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <AssignmentIcon sx={{ mr: 1 }} /> {t('JENIS NOTIFIKASI')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('PILIH JENIS NOTIFIKASI YANG INGIN ANDA TERIMA')}
            </Typography>
            
            <Grid container spacing={2}>
              {[
                'projectUpdates',
                'taskAssignments',
                'progressReports',
                'approvalRequests',
                'systemAnnouncements',
                'dailyDigest',
                'weeklyDigest'
              ].map((type) => (
                <Grid item xs={12} sm={6} md={4} key={type}>
                  <NotificationCard>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getNotificationIcon(type)}
                          <Typography variant="subtitle2" sx={{ ml: 1 }}>
                            {type === 'projectUpdates' && 'Pembaruan Proyek'}
                            {type === 'taskAssignments' && 'Penugasan Tugas'}
                            {type === 'progressReports' && 'Laporan Kemajuan'}
                            {type === 'approvalRequests' && 'Permintaan Persetujuan'}
                            {type === 'systemAnnouncements' && 'Pengumuman Sistem'}
                            {type === 'dailyDigest' && 'Ringkasan Harian'}
                            {type === 'weeklyDigest' && 'Ringkasan Mingguan'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Tooltip title={getNotificationInfo(type)}>
                            <IconButton size="small" sx={{ mr: 0.5 }}>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <NotificationSwitch
                            checked={settings[type]}
                            onChange={handleChange}
                            name={type}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </NotificationCard>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      
      <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationSettings;
